import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Trash2, Download, ArrowLeft, ArrowRight, SquarePlay } from 'lucide-react';
import { SyncTranscript } from 'components/SyncTranscript';
import {
    formatCallOutcome,
    getLeadStatusStyle,
    getOutcomeStyle,
    formatLeadStatus,
    getDispositionStyle,
    formatDisposition,
} from '../../utils';
import styles from './CallDetailsModal.module.scss';
import { Modal, Button } from 'common/components';
import { CallType } from 'types/call.type';
import { Disposition } from 'types/call.type';
import { PartyStages } from 'services/api/getPartyStages';
import { QualificationBadges } from 'components/AgentCallConsole/components/QualificationBadges';
import { AggregatedQualificationsAccordion } from 'components/AggregatedQualificationsAccordion';

import { LiveTranscript } from '../LiveTranscript';
import { useAuth } from 'contexts/AuthContext';
import { hasAnyRole, UserRole } from 'types/userRoles';
import { useGetQualifications } from 'services/hooks/mutations';
import { formatQualifications } from 'utils/livekitContext.utils';
import { useGetAgregatedQualifications } from 'services/hooks/mutations/useGetAgregatedQualifications';

export interface Lead {
    id: string;
    callId: string;
    callDate: string;
    callTime: string;
    // originalCallDate: string;
    outcome: keyof typeof CallType;
    duration: string;
    durationTooltip: string;
    crmStatus: keyof typeof PartyStages;
    crmId: string;
    name: string;
    number: string;
    transferTo: string;
    disposition: keyof typeof Disposition | '';
    callDirection?: string;
    action: string;
    recording: string;
    transcript: string;
    aggregatedTagsUsed: boolean;
}

interface CallDetailsModalProps {
    selectedCall: Lead | null;
    setSelectedCall: (call: Lead | null) => void;
    leads: Lead[];
    isOpen: boolean;
    onClose: () => void;
    handleDeleteCall: (params: { selectedCallId: string }) => void;
    handleExportCSV: (params: { selectedCallId: string }) => void;
    currentPage: number;
    handlePageChange: (page: number) => void;
    totalPages: number;
    isLoading: boolean;
    dispositionLabels?: Record<string, string>;
}

export const CallDetailsModal = ({
    selectedCall,
    setSelectedCall,
    leads,
    isOpen,
    onClose,
    handleDeleteCall,
    handleExportCSV,
    currentPage,
    handlePageChange,
    totalPages,
    isLoading,
    dispositionLabels,
}: CallDetailsModalProps) => {
    const { userRoles } = useAuth();
    const hasManagerRole = hasAnyRole(userRoles, [UserRole.MANAGER, UserRole.SUPER_ADMIN, UserRole.ADMIN]);

    const [isSyncTranscriptOpen, setIsSyncTranscriptOpen] = useState<boolean>(false);
    const [isCallNavigating, setIsCallNavigating] = useState(false);
    // Store local copy of selected call to preserve it when not in current page
    const [localSelectedCall, setLocalSelectedCall] = useState<Lead | null>(selectedCall);

    // Track if we're navigating (to prevent unwanted updates)
    const isNavigatingRef = useRef(false);
    const pendingNavigationRef = useRef<'next' | 'prev' | null>(null);

    const { getQualifications, data: qualificationsData } = useGetQualifications();
    const {
        getAgregatedQualifications,
        data: aggregatedQualificationsData,
        isPending: isAggregatedQualificationsPending,
    } = useGetAgregatedQualifications();

    const fetchQualificationsForCall = useCallback(
        (call: Pick<Lead, 'callId' | 'aggregatedTagsUsed'>) => {
            if (call.aggregatedTagsUsed) {
                getAgregatedQualifications(call.callId);
                return;
            }

            getQualifications(call.callId);
        },
        [getAgregatedQualifications, getQualifications]
    );

    const isAllNoAnswer = useMemo(
        () => qualificationsData?.every((qualification) => qualification.value === 'NO_ANSWER'),
        [qualificationsData]
    );
    const formattedQualifications = useMemo(() => formatQualifications(qualificationsData || []), [qualificationsData]);

    useEffect(() => {
        if (!isOpen) {
            setLocalSelectedCall(null);
            setIsCallNavigating(false);
            isNavigatingRef.current = false;
            pendingNavigationRef.current = null;
            return;
        }

        if (selectedCall && localSelectedCall?.callId !== selectedCall.callId) {
            setLocalSelectedCall(selectedCall);
        }
    }, [isOpen, selectedCall, localSelectedCall?.callId]);

    useEffect(() => {
        if (!isOpen || !localSelectedCall) return;

        fetchQualificationsForCall(localSelectedCall);
    }, [isOpen, localSelectedCall?.callId, localSelectedCall?.aggregatedTagsUsed, fetchQualificationsForCall]);

    // Sync local call details when the leads list updates, without re-fetching qualifications.
    useEffect(() => {
        if (!localSelectedCall || isNavigatingRef.current || !isOpen) return;

        const updatedCall = leads.find((lead) => lead.callId === localSelectedCall.callId);
        if (!updatedCall) return;

        const hasCallDetailsChanged =
            updatedCall.outcome !== localSelectedCall.outcome ||
            updatedCall.disposition !== localSelectedCall.disposition ||
            updatedCall.crmStatus !== localSelectedCall.crmStatus ||
            updatedCall.aggregatedTagsUsed !== localSelectedCall.aggregatedTagsUsed;

        if (hasCallDetailsChanged) {
            setLocalSelectedCall(updatedCall);
        }
    }, [leads, isOpen, localSelectedCall]);

    // Use localSelectedCall for navigation calculations
    const { previousCall, nextCall, currentIndex } = useMemo<{
        previousCall: Lead | undefined;
        nextCall: Lead | undefined;
        currentIndex: number;
    }>(() => {
        if (!localSelectedCall) return { previousCall: undefined, nextCall: undefined, currentIndex: -1 };

        const currentIndex = leads.findIndex((lead) => lead.callId === localSelectedCall.callId);
        if (currentIndex < 0) return { previousCall: undefined, nextCall: undefined, currentIndex: -1 };
        const previousCall = leads[currentIndex - 1];
        const nextCall = leads[currentIndex + 1];
        return { previousCall, nextCall, currentIndex };
    }, [localSelectedCall, leads]);

    // Handle page change completion - navigate to first/last item after page loads
    useEffect(() => {
        if (pendingNavigationRef.current && !isLoading && leads.length > 0 && localSelectedCall) {
            if (pendingNavigationRef.current === 'next') {
                if (currentIndex >= 0) {
                    // If found in new page, navigate to the next call
                    if (nextCall) {
                        setLocalSelectedCall(nextCall);
                        setSelectedCall(nextCall);
                    } else {
                        // If it's the last item on this page, keep showing it
                        setLocalSelectedCall(leads[currentIndex]);
                        setSelectedCall(leads[currentIndex]);
                    }
                } else {
                    // If not found, show first call on new page
                    setLocalSelectedCall(leads[0]);
                    setSelectedCall(leads[0]);
                }
            } else if (pendingNavigationRef.current === 'prev') {
                if (currentIndex >= 0) {
                    // If found in new page, navigate to the previous call
                    if (previousCall) {
                        setLocalSelectedCall(previousCall);
                        setSelectedCall(previousCall);
                    } else {
                        // If it's the first item on this page, keep showing it
                        setLocalSelectedCall(leads[leads.length - 1]);
                        setSelectedCall(leads[leads.length - 1]);
                    }
                } else {
                    // If not found, show last call on new page
                    setLocalSelectedCall(leads[leads.length - 1]);
                    setSelectedCall(leads[leads.length - 1]);
                }
            }

            pendingNavigationRef.current = null;
            isNavigatingRef.current = false;
        }
    }, [leads, isLoading, localSelectedCall, currentIndex, nextCall, previousCall, setSelectedCall]);

    const handleCallNavigationComplete = useCallback(() => {
        setIsCallNavigating(false);
    }, []);

    const navigateToCall = useCallback(
        (call: Lead) => {
            isNavigatingRef.current = true;
            if (isSyncTranscriptOpen) {
                setIsCallNavigating(true);
            }
            setLocalSelectedCall(call);
            setSelectedCall(call);
            isNavigatingRef.current = false;
        },
        [isSyncTranscriptOpen, setSelectedCall]
    );

    const handlePreviousCall = () => {
        if (previousCall) {
            navigateToCall(previousCall);
            return;
        }

        if (!previousCall && currentPage > 1) {
            isNavigatingRef.current = true;
            if (isSyncTranscriptOpen) {
                setIsCallNavigating(true);
            }
            pendingNavigationRef.current = 'prev';
            handlePageChange(currentPage - 1);
        }
    };

    const handleNextCall = () => {
        if (nextCall) {
            navigateToCall(nextCall);
            return;
        }

        if (!nextCall && currentPage < totalPages) {
            isNavigatingRef.current = true;
            if (isSyncTranscriptOpen) {
                setIsCallNavigating(true);
            }
            pendingNavigationRef.current = 'next';
            handlePageChange(currentPage + 1);
        }
    };

    const displayCall = localSelectedCall || selectedCall;
    if (!isOpen || !displayCall) return null;

    const footer = (
        <div className='flex justify-end gap-md'>
            <Button variant='outline-secondary' onClick={onClose}>
                Cancel
            </Button>
            <Button
                variant='primary'
                icon={Trash2}
                onClick={() => handleDeleteCall({ selectedCallId: displayCall.callId })}
                disabled={isLoading}
            >
                Delete call
            </Button>
            <Button
                variant='primary'
                icon={Download}
                onClick={() => handleExportCSV({ selectedCallId: displayCall.callId })}
                disabled={isLoading}
            >
                Export CSV
            </Button>
        </div>
    );

    const callDetailsNavigation = (
        <div className='flex justify-between items-center gap-md'>
            <Button
                variant='outline-secondary'
                onClick={handlePreviousCall}
                icon={ArrowLeft}
                disabled={(currentPage === 1 && !previousCall) || isLoading}
            >
                Prev call
            </Button>
            <Button
                variant='outline-secondary'
                onClick={handleNextCall}
                icon={ArrowRight}
                iconPosition='right'
                disabled={(currentPage === totalPages && !nextCall) || isLoading}
            >
                Next call
            </Button>
        </div>
    );

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                size='lg'
                showDefaultFooter={false}
                footer={footer}
                isLoadingOverlay={isLoading}
                title={
                    <div className='flex justify-between items-center'>
                        <h2>Call Details</h2>
                        {callDetailsNavigation}
                    </div>
                }
            >
                <div className='flex gap-md'>
                    <div className='flex flex-col flex-1'>
                        <div className={styles.callDetailsContainer}>
                            <div className={styles.group}>
                                <div className={styles.title}>Call date</div>
                                <div className={styles.value}>
                                    {displayCall.callDate} {displayCall.callTime}
                                </div>
                            </div>
                            <div className={styles.group}>
                                <div className={styles.title}>Call Status</div>
                                <div>
                                    <div style={getOutcomeStyle(displayCall.outcome)}>
                                        {formatCallOutcome(displayCall.outcome)}
                                    </div>
                                </div>
                            </div>

                            <div className={styles.group}>
                                <div className={styles.title}>Duration</div>
                                <div className={styles.value}>{displayCall.duration}</div>
                            </div>
                            <div className={styles.group}>
                                <div className={styles.title}>Party status</div>
                                <div className={styles.value}>
                                    <span style={getLeadStatusStyle(displayCall.crmStatus)}>
                                        {formatLeadStatus(displayCall.crmStatus)}
                                    </span>
                                </div>
                            </div>

                            <div className={styles.group}>
                                <div className={styles.title}>CRM ID</div>
                                <div className={styles.value}>{displayCall.crmId || '-'}</div>
                            </div>
                            <div className={styles.group}>
                                <div className={styles.title}>Name</div>
                                <div className={styles.value}>{displayCall.name}</div>
                            </div>

                            <div className={styles.group}>
                                <div className={styles.title}>Number</div>
                                <div className={styles.value}>{displayCall.number}</div>
                            </div>
                            <div className={styles.group}>
                                <div className={styles.title}>Transfer to</div>
                                <div className={styles.value}>{displayCall.transferTo || '-'}</div>
                            </div>

                            <div className={styles.group}>
                                <div className={styles.title}>Disposition</div>
                                <div className={styles.value}>
                                    {displayCall.disposition ? (
                                        <span style={getDispositionStyle(displayCall.disposition)}>
                                            {formatDisposition(displayCall.disposition, dispositionLabels)}
                                        </span>
                                    ) : (
                                        <span>-</span>
                                    )}
                                </div>
                            </div>
                            <div className={styles.group}>
                                <div className={styles.title}>Call Direction</div>
                                <div className={styles.value}>{displayCall.callDirection || '-'}</div>
                            </div>
                            <div className={styles.group}>
                                <div className={styles.title}>Call ID</div>
                                <div className={styles.value}>{displayCall.callId || '-'}</div>
                            </div>
                        </div>

                        <div className={styles.callDetailsContainer}>
                            <div className={styles.group}>
                                <div className={styles.title}>Transcript & Recording</div>
                                <div className={styles.value}>
                                    <Button
                                        variant='outline-secondary'
                                        size='sm'
                                        iconOnly
                                        isRounded
                                        icon={SquarePlay}
                                        onClick={() => setIsSyncTranscriptOpen(true)}
                                        title='Listen to recording'
                                    />
                                </div>
                            </div>

                            {hasManagerRole && (
                                <div className={styles.group}>
                                    <div className={styles.title}>Live Transcript</div>
                                    <div className={styles.value}>
                                        <LiveTranscript
                                            callID={displayCall.callId}
                                            isCallInProgress={displayCall.outcome === CallType.IN_PROGRESS}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div style={{ width: '45%', display: 'grid' }}>
                        {displayCall.aggregatedTagsUsed ? (
                            <AggregatedQualificationsAccordion
                                qualifications={aggregatedQualificationsData}
                                isLoading={isAggregatedQualificationsPending}
                            />
                        ) : (
                            <QualificationBadges
                                isAllNoAnswer={isAllNoAnswer}
                                qualifications={formattedQualifications}
                            />
                        )}
                    </div>
                </div>
            </Modal>

            <SyncTranscript
                callId={displayCall.callId}
                isOpen={isSyncTranscriptOpen}
                onClose={() => {
                    setIsSyncTranscriptOpen(false);
                    setIsCallNavigating(false);
                }}
                callInfo={displayCall}
                callDetailsNavigation={callDetailsNavigation}
                isLoading={isLoading}
                isCallNavigating={isCallNavigating}
                onCallNavigationComplete={handleCallNavigationComplete}
            />
        </>
    );
};
